import { useQuery } from '@tanstack/react-query';
import type { CoverageCell, TestType } from 'fuel-benchmark-contracts';
import { useNavigate } from 'react-router-dom';
import { orpc } from '../../../apis/orpc-query';
import { Spinner } from '../../../components/ui/Spinner';
import { StatusText } from '../../../components/ui/StatusText';
import { cn } from '../../../lib/cn';
import { formatDecimal } from '../../../lib/format';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { trafficSummary } from '../../conditions/condition-display';
import { COVERAGE_GLYPHS, coverageTone } from '../../runs/run-display';

export interface CoverageTabProps {
  vehicleId: string;
}

const TEST_TYPE_ORDER: readonly TestType[] = ['DRIVE', 'IDLE'];

/**
 * The scenario coverage matrix of plan §7: rows are scenarios grouped by test type, and a cell carries the
 * verdict glyph (`✓` / `!` / `×` / `—`), how many valid runs back it, and the mean measured figure. Everything
 * stays muted except the two states worth chasing — a warning and a failure.
 */
export const CoverageTab = ({ vehicleId }: CoverageTabProps) => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();

  const coverage = useQuery(orpc.benchmarks.coverageMatrix.queryOptions({ input: { vehicleId }, enabled: vehicleId !== '' }));

  if (coverage.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner label={t('app.loading')} />
        {t('app.loading')}
      </div>
    );
  }

  if (coverage.isError || !coverage.data) {
    return (
      <p
        role="alert"
        className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
      >
        {t('errors.loadFailed')}
      </p>
    );
  }

  const cells = coverage.data.cells;
  const tested = cells.filter((cell) => cell.status !== 'NOT_TESTED').length;

  const conditionsOf = (cell: CoverageCell): string =>
    [
      t(`enums.season.${cell.season}`),
      t(`enums.timeBand.${cell.timeBand}`),
      t(`enums.hvacState.${cell.hvacState}`),
      cell.testType === 'IDLE' ? `${cell.idleDurationMin ?? '?'} min` : t(`enums.routeType.${cell.routeType ?? 'MIXED'}`),
      trafficSummary(cell.trafficLevel, t),
    ]
      .filter((part): part is string => part !== null)
      .join(' · ');

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        {t('vehicles.coverage.summary', { tested, total: cells.length })} {t('vehicles.coverage.legend')}
      </p>

      {TEST_TYPE_ORDER.map((testType) => {
        const rows = cells.filter((cell) => cell.testType === testType);

        if (rows.length === 0) {
          return null;
        }

        return (
          <section
            key={testType}
            className="space-y-2"
          >
            <h3 className="text-sm font-medium text-foreground">{t(`enums.testType.${testType}`)}</h3>

            <div className="overflow-x-auto rounded-lg border border-border bg-card">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {[
                      t('vehicles.coverage.columns.scenario'),
                      t('vehicles.coverage.columns.conditions'),
                      t('vehicles.coverage.columns.status'),
                      t('vehicles.coverage.columns.validRuns'),
                      t('vehicles.coverage.columns.mean'),
                      t('vehicles.coverage.columns.best'),
                    ].map((header) => (
                      <th
                        key={header}
                        scope="col"
                        className="whitespace-nowrap px-4 py-3 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((cell) => (
                    <tr
                      key={cell.scenarioId}
                      onClick={() => navigate(`/runs?vehicleId=${vehicleId}&scenarioId=${cell.scenarioId}`)}
                      className="cursor-pointer border-b border-border transition-colors last:border-b-0 hover:bg-muted"
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-start font-mono text-xs text-foreground">
                        {cell.scenarioCode}
                        {cell.isCore ? null : (
                          <span className="ms-2 font-sans text-xs text-muted-foreground">{t('conditions.scenarios.supplementary')}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-start text-muted-foreground">{conditionsOf(cell)}</td>
                      <td className="px-4 py-3 text-start">
                        <span className="inline-flex items-center gap-2">
                          <span
                            aria-hidden
                            className={cn('font-mono text-base', cell.status === 'NOT_TESTED' ? 'text-muted-foreground' : undefined)}
                            style={cell.status === 'NOT_TESTED' ? undefined : { color: coverageColor(cell.status) }}
                          >
                            {COVERAGE_GLYPHS[cell.status]}
                          </span>
                          <StatusText
                            label={t(`enums.coverage.${cell.status}`)}
                            tone={coverageTone(cell.status)}
                          />
                        </span>
                      </td>
                      <td className="px-4 py-3 text-start text-foreground">
                        {cell.validRunCount}
                        {cell.totalRunCount > cell.validRunCount ? <span className="text-muted-foreground"> / {cell.totalRunCount}</span> : null}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-start font-medium text-foreground">
                        {cell.meanResult === null ? t('common.notSet') : `${formatDecimal(cell.meanResult, 1)} ${cell.unit}`}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-start text-muted-foreground">
                        {cell.bestResult === null ? t('common.notSet') : `${formatDecimal(cell.bestResult, 1)} ${cell.unit}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
};

/** The glyph carries the same colour its label does; `NOT_TESTED` is handled by the muted class instead. */
const coverageColor = (status: CoverageCell['status']): string => {
  switch (status) {
    case 'VALID':
      return '#15803d';
    case 'VALID_WITH_WARNING':
      return '#a16207';
    case 'INVALID':
      return '#dc2626';
    case 'NOT_TESTED':
      return 'inherit';
  }
};
